import { faChevronDown, faHashtag } from "@fortawesome/free-solid-svg-icons";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import type {
  Dispatch,
  FunctionComponent,
  ReactElement,
  SetStateAction,
} from "react";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";

import SiteMapContext from "../../context/site-map-context";
import type { SiteMapPage, SiteMapPageSection } from "../../lib/sitemap";
import { FontAwesomeIcon } from "../icons";
import { Link } from "../link";
import { Search } from "../pages/docs/search";
import { generatePathWithoutParams } from "../shared";
import {
  getNavbarLinkHref,
  getNavbarLinkTitle,
  itemIsPage,
  NAVBAR_LINK_ICONS,
} from "./util";

/**
 * Pages marked `hiddenFromSidebar: true` in their MDX frontmatter are still
 * routable but should not appear in nav. Filter them out wherever the mobile
 * nav iterates a list of pages.
 */
const visiblePages = (pages: SiteMapPage[] | undefined): SiteMapPage[] =>
  (pages ?? []).filter((page) => !page.hiddenFromSidebar);

type MobileNavNestedPageProps<T extends SiteMapPage | SiteMapPageSection> = {
  icon?: ReactElement;
  item: T;
  hydrationFriendlyAsPath: string;
  parentPageHref: T extends SiteMapPageSection ? string : undefined;
  depth?: number;
  expandedItems: { href: string; depth: number }[];
  setExpandedItems: Dispatch<SetStateAction<{ href: string; depth: number }[]>>;
  onClose: () => void;
};

const MobileNavNestedPage = <T extends SiteMapPage | SiteMapPageSection>({
  icon,
  depth = 0,
  expandedItems,
  parentPageHref,
  setExpandedItems,
  item,
  onClose,
  hydrationFriendlyAsPath,
}: MobileNavNestedPageProps<T>) => {
  const pathWithoutParams = generatePathWithoutParams(hydrationFriendlyAsPath);

  const { title } = item;
  const displayTitle = getNavbarLinkTitle(title);

  const isRoot = depth === 0;

  const href = itemIsPage(item)
    ? getNavbarLinkHref({ href: item.href, title })
    : `${parentPageHref}#${item.anchor}`;

  const isSelected = pathWithoutParams === href;

  const hasChildren = itemIsPage(item)
    ? visiblePages(item.subPages).length > 0 || (item.sections ?? []).length > 0
    : item.subSections.length > 0;

  const isOpen =
    hasChildren &&
    expandedItems.some(
      (expandedItem) =>
        expandedItem.href === href && expandedItem.depth === depth,
    );

  return (
    <>
      <Link href={href}>
        <ListItemButton
          selected={isSelected}
          onClick={() => {
            if (hasChildren && !isOpen) {
              setExpandedItems((prev) => [...prev, { href, depth }]);
            }
            onClose();
          }}
          sx={(theme) => ({
            backgroundColor: isRoot ? undefined : theme.palette.gray[20],
            "&.Mui-selected": {
              backgroundColor: isRoot ? undefined : theme.palette.gray[20],
              "&:hover": {
                backgroundColor: isRoot ? undefined : theme.palette.gray[40],
              },
            },
            "&:hover": {
              backgroundColor: isRoot ? undefined : theme.palette.gray[40],
            },
            pl: (icon ? 2 : 4) + depth * 2,
          })}
        >
          {icon || !itemIsPage(item) ? (
            <ListItemIcon
              sx={(theme) => ({
                minWidth: isRoot ? undefined : theme.spacing(3),
              })}
            >
              {icon ?? (
                <FontAwesomeIcon
                  icon={faHashtag}
                  sx={{ color: "inherit", fontSize: 15 }}
                />
              )}
            </ListItemIcon>
          ) : null}
          <ListItemText
            primary={displayTitle}
            sx={{
              wordBreak: "break-word",
              "> .MuiListItemText-primary": {
                display: "inline",
              },
            }}
          />
          {hasChildren ? (
            <IconButton
              sx={{
                transition: (theme) => theme.transitions.create("transform"),
                transform: `rotate(${isOpen ? "0deg" : "-90deg"})`,
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setExpandedItems((prev) =>
                  isOpen
                    ? prev.filter(
                        (expandedItem) =>
                          !(
                            expandedItem.href === href &&
                            expandedItem.depth === depth
                          ),
                      )
                    : [...prev, { href, depth }],
                );
              }}
            >
              <FontAwesomeIcon
                sx={{
                  fontSize: 15,
                }}
                icon={faChevronDown}
              />
            </IconButton>
          ) : null}
        </ListItemButton>
      </Link>
      {hasChildren ? (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {(itemIsPage(item) ? item.sections : item.subSections)?.map(
              (subSection) => (
                <MobileNavNestedPage<SiteMapPageSection>
                  hydrationFriendlyAsPath={hydrationFriendlyAsPath}
                  key={subSection.anchor}
                  depth={depth + 1}
                  parentPageHref={
                    itemIsPage(item) ? item.href : (parentPageHref as string)
                  }
                  item={subSection}
                  expandedItems={expandedItems}
                  setExpandedItems={setExpandedItems}
                  onClose={onClose}
                />
              ),
            )}
            {itemIsPage(item)
              ? visiblePages(item.subPages).map((subPage) => (
                  <MobileNavNestedPage<SiteMapPage>
                    hydrationFriendlyAsPath={hydrationFriendlyAsPath}
                    key={subPage.href}
                    depth={depth + 1}
                    item={subPage}
                    parentPageHref={undefined}
                    expandedItems={expandedItems}
                    setExpandedItems={setExpandedItems}
                    onClose={onClose}
                  />
                ))
              : null}
          </List>
          {depth === 0 ? <Divider /> : null}
        </Collapse>
      ) : null}
    </>
  );
};

type MobileNavItemsProps = {
  hydrationFriendlyAsPath: string;
  onClose: () => void;
};

const getInitialExpandedItems = ({
  hydrationFriendlyAsPath,
  parentHref,
  item,
  depth = 0,
}: {
  hydrationFriendlyAsPath: string;
  parentHref?: string;
  item: SiteMapPage | SiteMapPageSection;
  depth?: number;
}): { href: string; depth: number }[] => {
  const pathWithoutParams = generatePathWithoutParams(hydrationFriendlyAsPath);

  const expandedChildren = [
    ...(itemIsPage(item)
      ? visiblePages(item.subPages)
          .map((page) =>
            getInitialExpandedItems({
              item: page,
              hydrationFriendlyAsPath,
              depth: depth + 1,
            }),
          )
          .flat()
      : []),
    ...((itemIsPage(item) ? item.sections : item.subSections) ?? [])
      .map((section) =>
        getInitialExpandedItems({
          item: section,
          hydrationFriendlyAsPath,
          depth: depth + 1,
          parentHref: itemIsPage(item)
            ? getNavbarLinkHref({ href: item.href, title: item.title })
            : parentHref,
        }),
      )
      .flat(),
  ];

  const href = itemIsPage(item)
    ? getNavbarLinkHref({ href: item.href, title: item.title })
    : `${parentHref}#${item.anchor}`;

  const isExpanded = pathWithoutParams === href || expandedChildren.length > 0;

  return isExpanded
    ? [
        {
          href,
          depth,
        },
        ...expandedChildren,
      ]
    : [];
};

export const MobileNavItems: FunctionComponent<MobileNavItemsProps> = ({
  onClose,
  hydrationFriendlyAsPath,
}) => {
  const { pages: pagesRaw } = useContext(SiteMapContext);
  // Memoize so the filtered array's reference is stable across renders;
  // the `useEffect` below depends on `pages`, so a fresh array each render
  // would trigger an infinite `setExpandedItems` loop.
  const pages = useMemo(() => visiblePages(pagesRaw), [pagesRaw]);

  const [expandedItems, setExpandedItems] = useState<
    { href: string; depth: number }[]
  >(
    pages
      .map((page) =>
        getInitialExpandedItems({ hydrationFriendlyAsPath, item: page }),
      )
      .flat(),
  );

  useEffect(() => {
    setExpandedItems((prev) => [
      ...prev,
      ...pages
        .map((page) =>
          getInitialExpandedItems({ hydrationFriendlyAsPath, item: page }),
        )
        .flat()
        .filter(
          (expanded) =>
            prev.find(
              ({ depth, href }) =>
                expanded.depth === depth && expanded.href === href,
            ) === undefined,
        ),
    ]);
  }, [hydrationFriendlyAsPath, pages]);

  return (
    <List>
      <Box m={2}>
        <Search variant="mobile" />
      </Box>

      {pages.map((page) => (
        <Fragment key={page.href}>
          <MobileNavNestedPage<SiteMapPage>
            hydrationFriendlyAsPath={hydrationFriendlyAsPath}
            key={page.href}
            icon={NAVBAR_LINK_ICONS[page.title]}
            item={page}
            parentPageHref={undefined}
            expandedItems={expandedItems}
            setExpandedItems={setExpandedItems}
            onClose={onClose}
          />
        </Fragment>
      ))}
    </List>
  );
};
